import * as React from "react";
import { cn } from "../../utils/cn";

export type FileInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> & {
  testId?: string;
};

/**
 * A file picker that looks like the control it is.
 *
 * `Input` styles `::file-selector-button` as an afterthought: the browser
 * default keeps its own height inside a 44px box, so the button floats off
 * the text baseline of the fields above and below it. Here the button fills
 * the control, sits flush in its left corner, and carries the hover and
 * pointer affordances of a button.
 *
 * The rendered element stays a bare `input`, so `accept`, `disabled`, a ref
 * and the change event all behave as they do anywhere else.
 */
const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ className, testId, ...props }, ref) => {
    return (
      <input
        ref={ref}
        type="file"
        className={cn(
          "flex h-11 w-full cursor-pointer rounded-md border border-input bg-background pr-4 text-sm leading-[2.625rem] text-foreground ring-offset-background",
          "file:mr-4 file:h-[2.625rem] file:cursor-pointer file:rounded-l-md file:border-0 file:bg-muted file:px-4 file:text-sm file:font-semibold file:text-foreground",
          "hover:file:bg-border focus:border-ring focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/30",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:file:cursor-not-allowed",
          className,
        )}
        data-testid={testId}
        {...props}
      />
    );
  },
);

FileInput.displayName = "FileInput";

export { FileInput };
